import { useState, useMemo } from 'react';

/**
 * Tiny sortable-table helper.
 *
 * Usage:
 *   const { rows, sortBy, sortDir, handleSort, sortProps } = useSortableTable(
 *     data,
 *     'created_at',
 *     'desc',
 *     { name: (r) => r.name?.toLowerCase() }, // optional accessors per column
 *   );
 *   <TableSortLabel {...sortProps('name')}>Name</TableSortLabel>
 *
 * If no accessor is provided for a column, the value is read directly off
 * the row by key. Strings are compared case-insensitively, numbers / dates
 * use their natural ordering. Nulls always sort last.
 */
export default function useSortableTable(data, defaultKey, defaultDir = 'asc', accessors = {}) {
  const [sortBy, setSortBy] = useState(defaultKey);
  const [sortDir, setSortDir] = useState(defaultDir);

  const handleSort = (key) => {
    if (key === sortBy) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortBy(key);
      setSortDir('asc');
    }
  };

  const rows = useMemo(() => {
    if (!Array.isArray(data) || !sortBy) return data || [];
    const accessor = accessors[sortBy] || ((r) => r?.[sortBy]);
    const dir = sortDir === 'asc' ? 1 : -1;
    return [...data].sort((a, b) => {
      const va = accessor(a);
      const vb = accessor(b);
      // Nulls last regardless of direction
      const aNull = va == null;
      const bNull = vb == null;
      if (aNull && bNull) return 0;
      if (aNull) return 1;
      if (bNull) return -1;
      // Numeric / date / boolean
      if (typeof va === 'number' && typeof vb === 'number') return (va - vb) * dir;
      if (va instanceof Date && vb instanceof Date) return (va - vb) * dir;
      // String fallback (lowercased for case-insensitive sort)
      const sa = String(va).toLowerCase();
      const sb = String(vb).toLowerCase();
      if (sa < sb) return -1 * dir;
      if (sa > sb) return 1 * dir;
      return 0;
    });
  }, [data, sortBy, sortDir, accessors]);

  const sortProps = (key) => ({
    active: sortBy === key,
    direction: sortBy === key ? sortDir : 'asc',
    onClick: () => handleSort(key),
  });

  return { rows, sortBy, sortDir, handleSort, sortProps };
}
